import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useChecklistTemplates, useDomains } from '@/lib/queries';
import { Card, CardContent } from '@/components/ui/card';
import TemplateGallery from '@/components/TemplateGallery';
import { useAuth } from '@/lib/auth-context';

/**
 * Templates gallery — the second tier of the Checklists shape. Top-
 * of-page CTA goes to the wizard; below it, the existing per-domain
 * gallery cards. Reads as the "binder shelf" — pick a sequence to
 * start, or write a new one.
 */
export default function ChecklistTemplates() {
  const templates = useChecklistTemplates();
  const domains = useDomains();
  const { role } = useAuth();
  const canCreate = role === 'owner' || role === 'admin';

  return (
    <div className="space-y-8">
      <header className="space-y-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
          <Link to="/checklists" className="hover:text-foreground">
            Checklists
          </Link>{' '}
          · Templates
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">
          Templates
        </h1>
        <p className="text-muted-foreground max-w-2xl">
          Pick a template to start a run, or write a new one. Templates capture the
          sequence; runs capture what you actually did.
        </p>
      </header>

      {canCreate ? (
        <section aria-labelledby="create-cta">
          <h2 id="create-cta" className="sr-only">
            Create a template
          </h2>
          <Link
            to="/checklists/templates/new"
            className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="cta-create-template"
          >
            <Card className="border-primary/30 bg-primary/5 transition-colors hover:bg-primary/10">
              <CardContent className="flex items-center gap-3 p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Plus className="h-5 w-5" aria-hidden />
                </span>
                <div className="space-y-0.5">
                  <p className="font-display text-base">Create new template</p>
                  <p className="text-sm text-muted-foreground">
                    Capture a new repeatable sequence.
                  </p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </section>
      ) : null}

      <section className="space-y-3" aria-labelledby="gallery">
        <div className="flex items-baseline justify-between border-b border-rule pb-2">
          <h2 id="gallery" className="font-display text-xl font-semibold tracking-tight">
            By domain
          </h2>
        </div>
        {templates.isLoading || domains.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : null}
        {templates.data ? (
          <TemplateGallery
            templates={templates.data}
            domainName={(id) =>
              (domains.data ?? []).find((d) => d.id === id)?.name ?? id
            }
          />
        ) : null}
      </section>
    </div>
  );
}
